import { useEffect, useState } from "react";
import { Save, Loader2, Droplets, Pencil, Trash2, Plus, X, AlertTriangle } from "lucide-react";import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

function emptyTariff(carrier = "") {
  return {
    carrier_code: carrier,
    currency: "USD",
    free_days: 45,
    handling_20: 0, handling_40: 0,
    storage_per_day_20: 0, storage_per_day_40: 0,
    inspection_20: 0, inspection_40: 0,
    washing_20: 0, washing_40: 0,
    special_cleaning_20: 0, special_cleaning_40: 0,
    short_pti_20: 0, short_pti_40: 0,
    extended_pti_20: 0, extended_pti_40: 0,
    food_upgrade_20: 0, food_upgrade_40: 0,
    pti_fee_20: 0, pti_fee_40: 0,
    power_per_day_20: 0, power_per_day_40: 0,
    storage_mode: "PER_DAY",
    handling_mode: "PER_EVENT",
  };
}

const TARIFF_ROWS = [
  { label: "Handling", k: "handling" },
  { label: "Storage / day (after free days)", k: "storage_per_day" },
  { label: "PTI flat fee", k: "pti_fee" },
  { label: "Power supply / day", k: "power_per_day" },
  { label: "Inspection Fee", k: "inspection" },
  { label: "Special Cleaning", k: "special_cleaning" },
  { label: "Short PTI (Visual)", k: "short_pti" },
  { label: "Extended PTI", k: "extended_pti" },
  { label: "Food Upgrade", k: "food_upgrade" },
];

export default function Tariffs() {
  const { user } = useAuth();
  const canEditWashPricing = ["admin", "supervisor"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [wsTariffs, setWsTariffs] = useState([]); // [{ customer_code, flat_fee, currency, active }]
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [partnerDefaultCurrency, setPartnerDefaultCurrency] = useState(null);

  // Water Solution flat fees are stored in a separate collection but shown
  // inline on the carrier tariff — editing + listing both read/write both
  // sources, so finance only has one place to manage carrier pricing.
  const wsFeeFor = (code) => {
    const t = wsTariffs.find((w) => w.customer_code === code && w.active !== false);
    return t ? Number(t.flat_fee) : null;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: ws }] = await Promise.all([
        api.get("/tariffs"),
        api.get("/water-solutions/tariffs"),
      ]);
      setItems(data);
      setWsTariffs(Array.isArray(ws) ? ws : []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // When editor opens or carrier changes, pull the partner's default_currency
  // so we can warn if the tariff diverges — and auto-adopt it when creating
  // a brand-new tariff (so EUR partners stay sticky without touching the
  // "Align all rates" button).
  useEffect(() => {
    const cc = (editing?.carrier_code || "").toUpperCase().trim();
    if (!cc || cc.length < 2) { setPartnerDefaultCurrency(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/partners/${cc}`);
        if (!alive) return;
        const def = (data?.default_currency || "").toUpperCase() || null;
        setPartnerDefaultCurrency(def);
        // Auto-adopt on NEW tariffs only — never silently rewrite an existing
        // divergent currency, that would mask operator intent.
        const isNew = !items.some((t) => t.carrier_code === cc);
        if (isNew && def && editing && (editing.currency || "USD").toUpperCase() === "USD" && def !== "USD") {
          setEditing((e) => (e ? { ...e, currency: def } : e));
        }
      } catch { if (alive) setPartnerDefaultCurrency(null); }
    })();
    return () => { alive = false; };
  }, [editing?.carrier_code, items]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      // Water Solution fee is managed via a dedicated endpoint (separate
      // collection). Strip it from the carrier-tariff payload and PUT it
      // after the main save succeeds, so one Save button covers both.
      const wsFee = Number(payload.ws_flat_fee || 0);
      const wsRoles = payload._carrier_roles || null; // injected on open
      delete payload.ws_flat_fee;
      delete payload._carrier_roles;
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      await api.put(`/tariffs/${editing.carrier_code}`, payload);

      // Only push WS if there's a value AND the partner can actually carry
      // the role (backend rejects non-carriers). On brand-new tariffs we
      // skip the WS call if the field is still 0 and no row existed, so
      // finance isn't forced to pick a WS fee for carriers that don't offer
      // the service.
      const prevWs = wsTariffs.find((w) => w.customer_code === editing.carrier_code);
      const changed = !prevWs || Number(prevWs.flat_fee) !== wsFee;
      if (changed && (wsFee > 0 || prevWs)) {
        try {
          await api.put(`/water-solutions/tariffs/${editing.carrier_code}`, {
            customer_code: editing.carrier_code,
            flat_fee: wsFee,
            currency: (editing.currency || "USD").toUpperCase(),
            active: wsFee > 0,
          });
        } catch (e) {
          // Non-fatal — the carrier tariff saved, surface the WS error so
          // finance can fix the role / value without losing the main save.
          toast.error("Carrier tariff saved. Water Solution fee failed: " + formatApiError(e));
          setEditing(null);
          load();
          return;
        }
      }

      toast.success(`Tariff for ${editing.carrier_code} saved`);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (editing) {
    const set = (k, v) => setEditing((f) => ({ ...f, [k]: v }));
    return (
      <div data-testid="tariffs-page">
        <PageHeader
          title="Tariffs"
          subtitle="Per-carrier rates used by the invoice generator."
          testid="tariffs-header"
        />
        <div className="p-6 sm:p-8" data-testid="tariff-editor">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Tariff editor</div>
              <h2 className="font-display text-xl font-extrabold">{editing.carrier_code || "New tariff"}</h2>
            </div>
            <div className="flex gap-2">
              <button className="ps-btn ps-btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="ps-btn ps-btn-primary" onClick={save} disabled={saving} data-testid="save-tariff">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save tariff</>}
              </button>
            </div>
          </div>
          <div className="border border-gray-200 bg-white p-5 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Carrier code</label>
                <input value={editing.carrier_code} onChange={(e) => set("carrier_code", e.target.value.toUpperCase())} className="ps-input font-mono" data-testid="tariff-carrier" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Currency</label>
                <input value={editing.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} className="ps-input font-mono" maxLength={3} data-testid="tariff-currency" />
                {partnerDefaultCurrency && (editing.currency || "").toUpperCase() !== partnerDefaultCurrency && (
                  <div className="mt-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1.5" data-testid="tariff-currency-divergence">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>Partner default is <b>{partnerDefaultCurrency}</b>. Change the partner default or set this tariff to match to avoid mixed-currency invoices.</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Free days</label>
                <input type="number" min={0} value={editing.free_days} onChange={(e) => set("free_days", parseInt(e.target.value || "0", 10))} className="ps-input font-mono" data-testid="tariff-free-days" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                  Storage billing mode
                </label>
                <select
                  value={editing.storage_mode || "PER_DAY"}
                  onChange={(e) => set("storage_mode", e.target.value)}
                  className="ps-input font-mono"
                  data-testid="tariff-storage-mode"
                >
                  <option value="PER_DAY">Per Day — charge only at gate-OUT (legacy)</option>
                  <option value="MONTHLY_CONTINUOUS">Monthly Continuous — bill every day in depot within the period</option>
                </select>
                <p className="text-[10px] text-gray-500 mt-1">
                  Use <strong>Monthly Continuous</strong> for TRIDENT / STOLT / ITT / LINER who bill while containers sit on the yard.
                </p>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                  Handling billing mode
                </label>
                <select
                  value={editing.handling_mode || "PER_EVENT"}
                  onChange={(e) => set("handling_mode", e.target.value)}
                  className="ps-input font-mono"
                  data-testid="tariff-handling-mode"
                >
                  <option value="PER_EVENT">Per Event — IN and OUT each charged (legacy)</option>
                  <option value="PER_LIFT">Per Lift — round-trip IN+OUT deduped to 1 charge</option>
                </select>
                <p className="text-[10px] text-gray-500 mt-1">
                  Use <strong>Per Lift</strong> when one round-trip counts as a single handling lift.
                </p>
              </div>
            </div>

            <table className="ps-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th className="text-right">20ft rate</th>
                  <th className="text-right">40ft rate</th>
                </tr>
              </thead>
              <tbody>
                {TARIFF_ROWS.map((r) => (
                  <tr key={r.k}>
                    <td className="font-semibold">{r.label}</td>
                    <td className="text-right">
                      <input
                        type="number" step="0.01" min={0}
                        value={editing[`${r.k}_20`] ?? 0}
                        onChange={(e) => set(`${r.k}_20`, parseFloat(e.target.value || "0"))}
                        className="ps-input font-mono text-right w-32 ml-auto"
                        data-testid={`tariff-${r.k}-20`}
                      />
                    </td>
                    <td className="text-right">
                      <input
                        type="number" step="0.01" min={0}
                        value={editing[`${r.k}_40`] ?? 0}
                        onChange={(e) => set(`${r.k}_40`, parseFloat(e.target.value || "0"))}
                        className="ps-input font-mono text-right w-32 ml-auto"
                        data-testid={`tariff-${r.k}-40`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-5 pt-4 border-t border-gray-100">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="min-w-[240px]">
                  <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1 flex items-center gap-1.5">
                    <Droplets className="w-3.5 h-3.5 text-[#006E9E]" /> Water Solution flat fee
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-500">
                      {(editing.currency || "USD").toUpperCase() === "EUR" ? "€" : "$"}
                    </span>
                    <input
                      type="number" step="0.01" min={0}
                      value={editing.ws_flat_fee ?? 0}
                      onChange={(e) => set("ws_flat_fee", parseFloat(e.target.value || "0"))}
                      className="ps-input font-mono text-right w-40"
                      placeholder="0.00"
                      data-testid="tariff-ws-flat-fee"
                    />
                    <span className="text-[11px] text-gray-500">per tank</span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1 max-w-md">
                    Lightweight rinse billed as a flat fee — invoiced on a separate monthly
                    Water Solution invoice. Set 0 to disable for this carrier.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 rounded-sm bg-blue-50 border border-blue-200 text-xs text-blue-900" data-testid="tariff-wash-hint">
              <Droplets className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
              <strong>Wash rates</strong> are not per-carrier — they follow the tank type (food vs chemical) and are
              set depot-wide at the top of the Tariffs page.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="tariffs-page">
      <PageHeader
        title="Tariffs"
        subtitle="Per-carrier rates used by the invoice generator."
        testid="tariffs-header"
        actions={
          <button className="ps-btn ps-btn-primary" onClick={() => setEditing({ ...emptyTariff(), ws_flat_fee: 0 })} data-testid="add-tariff">
            New tariff
          </button>
        }
      />
      <div className="p-6 sm:p-8 space-y-4">
        <div className="border border-blue-200 bg-blue-50/40 px-4 py-3 rounded-sm text-sm" data-testid="wash-pricing-moved-notice">
          <span className="font-bold text-blue-900">Wash pricing lives on Cargo Products →</span>{" "}
          <a href="/cargo-products" className="text-blue-800 underline underline-offset-2 hover:no-underline">
            open Cargo Products
          </a>
          <span className="text-gray-600">
            {" "}— depot rate card (FOOD / CHEMICAL / WATER), customer overrides, and per-product multi-currency rates
            are all managed there. This page keeps the per-carrier storage / handling / PTI rates that the invoice generator uses.
          </span>
        </div>
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="ps-table" data-testid="tariffs-table">
            <thead>
              <tr>
                <th>Carrier</th>
                <th>Currency</th>
                <th className="text-right">Free days</th>
                <th className="text-right">Handling 20/40</th>
                <th className="text-right">Storage/day 20/40</th>
                <th className="text-right">PTI Ext 20/40</th>
                <th className="text-right">Water Sol.</th>
                <th className="text-right">Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-gray-400">No tariffs yet. Add one to enable invoicing.</td></tr>}
              {!loading && items.map((t) => (
                <tr key={t.carrier_code} data-testid={`tariff-row-${t.carrier_code}`}>
                <td className="font-mono font-bold">
                  {t.carrier_code}
                  {(t.storage_mode === "MONTHLY_CONTINUOUS" || t.handling_mode === "PER_LIFT") && (
                    <div className="mt-1 flex gap-1 flex-wrap">
                      {t.storage_mode === "MONTHLY_CONTINUOUS" && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#006E9E]/10 text-[#006E9E] rounded">Monthly</span>
                      )}
                      {t.handling_mode === "PER_LIFT" && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#F1471D]/10 text-[#F1471D] rounded">Per-Lift</span>
                      )}
                    </div>
                  )}
                </td>
                  <td className="font-mono">{t.currency}</td>
                  <td className="text-right font-mono">{t.free_days}</td>
                  <td className="text-right font-mono">{t.handling_20.toFixed(2)} / {t.handling_40.toFixed(2)}</td>
                  <td className="text-right font-mono">{t.storage_per_day_20.toFixed(2)} / {t.storage_per_day_40.toFixed(2)}</td>
                  <td className="text-right font-mono">{t.extended_pti_20.toFixed(2)} / {t.extended_pti_40.toFixed(2)}</td>
                  <td className="text-right font-mono">
                    {(() => {
                      const fee = wsFeeFor(t.carrier_code);
                      if (fee == null) return <span className="text-gray-300">—</span>;
                      const wsT = wsTariffs.find((w) => w.customer_code === t.carrier_code);
                      const sym = (wsT?.currency || "USD").toUpperCase() === "EUR" ? "€" : "$";
                      return `${sym}${fee.toFixed(2)}`;
                    })()}
                  </td>
                  <td className="text-right text-xs text-gray-500 font-mono">{new Date(t.updated_at).toLocaleDateString()}</td>
                  <td className="text-right">
                    <button className="ps-btn ps-btn-secondary h-7 px-3 text-xs" onClick={() => setEditing({ ...t, ws_flat_fee: wsFeeFor(t.carrier_code) ?? 0 })} data-testid={`edit-tariff-${t.carrier_code}`}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}




// Wash rate card + customer overrides was moved to /cargo-products
// (2026-09-02) so all wash pricing lives in one place. This module keeps
// the component intact and re-exports it so the Cargo Products page can
// render it — this file no longer renders it inline.
export const TYPE_COLORS = ["#16a34a", "#d97706", "#0e7490", "#7c3aed", "#be185d", "#4d7c0f"];

export function WashRateCard({ canEdit }) {
  const [card, setCard] = useState(null);
  const [summary, setSummary] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ovEditor, setOvEditor] = useState(null);

  const load = async () => {
    try {
      const [{ data: c }, { data: s }] = await Promise.all([
        api.get("/wash/rate-card"),
        api.get("/wash/summary"),
      ]);
      setCard(c);
      setSummary(s);
      setDraft({ currency: c.currency, types: c.types.map((t) => ({ ...t })) });
    } catch (e) {
      toast.error(formatApiError(e));
    }
    api.get("/wash-tariffs").then((r) => setOverrides(r.data?.items || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const cleaned = (draft.types || [])
      .map((t) => ({ code: (t.code || "").toUpperCase().trim(), label: (t.label || "").trim(), price: Number(t.price) || 0 }))
      .filter((t) => t.code && t.label);
    if (!cleaned.length) { toast.error("Add at least one wash type"); return; }
    setSaving(true);
    try {
      await api.put("/wash/rate-card", { currency: (draft.currency || "USD").toUpperCase(), types: cleaned });
      toast.success("Wash rate card saved");
      setEditing(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveOverride = async (payload) => {
    try {
      await api.put(`/wash-tariffs/${payload.customer_code}`, payload);
      toast.success(`Override saved for ${payload.customer_code}`);
      setOvEditor(null);
      const r = await api.get("/wash-tariffs");
      setOverrides(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteOverride = async (code) => {
    if (!window.confirm(`Remove the customer-specific wash rates for ${code}? Their washes will bill at the depot rate card.`)) return;
    try {
      await api.delete(`/wash-tariffs/${code}`);
      toast.success(`Override removed for ${code}`);
      setOverrides((o) => o.filter((t) => t.customer_code !== code));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!card) return null;
  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const setType = (i, k, v) => setDraft((d) => ({ ...d, types: d.types.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)) }));

  return (
    <div className="border border-blue-200 bg-blue-50/40 rounded-sm p-4" data-testid="wash-rate-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-blue-900">
            <Droplets className="w-3.5 h-3.5" /> Wash rate card — depot-wide
          </div>
          <div className="text-xs text-gray-600 mt-1">
            Operators pick one of these wash types when creating a work order — the price flows straight to the monthly
            wash invoice. Customer-specific overrides below win over the depot rate.
          </div>
        </div>
        {canEdit && !editing && (
          <button data-testid="edit-wash-rate-card" onClick={() => setEditing(true)} className="ps-btn ps-btn-secondary h-7 px-3 text-xs">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
        )}
      </div>

      {!editing ? (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-4">
          {card.types.map((t, i) => (
            <RateTile
              key={t.code}
              label={t.label}
              value={`${card.currency} ${fmt(t.price)}`}
              sub={summary ? `${summary.by_category?.[t.code] ?? 0} washes · ${card.currency} ${fmt(summary.revenue?.by_type?.[t.code])} billed` : null}
              color={TYPE_COLORS[i % TYPE_COLORS.length]}
              testid={`wash-rate-${t.code}`}
            />
          ))}
          <RateTile
            label="Total billed (historical + certified)"
            value={summary ? `${card.currency} ${fmt(summary.revenue?.total)}` : "—"}
            sub={summary?.by_category?.UNKNOWN ? `${summary.by_category.UNKNOWN} washes pending classification` : null}
            color="#006E9E"
            testid="wash-rate-total"
          />
        </div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="wash-rate-card-editor">
          <table className="w-full text-sm bg-white border border-gray-200">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.14em] text-gray-600 border-b border-gray-200">
                <th className="text-left py-1.5 px-2 w-40">Code</th>
                <th className="text-left py-1.5 px-2">Label (shown to operators)</th>
                <th className="text-right py-1.5 px-2 w-36">Price / wash</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {draft.types.map((t, i) => (
                <tr key={i} className="border-b border-gray-100 last:border-0">
                  <td className="py-1 px-2"><input className="ps-input font-mono uppercase" value={t.code} onChange={(e) => setType(i, "code", e.target.value.toUpperCase())} placeholder="WATER" data-testid={`rate-type-code-${i}`} /></td>
                  <td className="py-1 px-2"><input className="ps-input" value={t.label} onChange={(e) => setType(i, "label", e.target.value)} placeholder="Water cleaning" data-testid={`rate-type-label-${i}`} /></td>
                  <td className="py-1 px-2"><input type="number" step="0.01" min="0" className="ps-input text-right font-mono" value={t.price} onChange={(e) => setType(i, "price", e.target.value)} data-testid={`rate-type-price-${i}`} /></td>
                  <td className="py-1 px-2 text-right">
                    <button type="button" onClick={() => setDraft((d) => ({ ...d, types: d.types.filter((_, idx) => idx !== i) }))} className="text-rose-600" title="Remove type" data-testid={`rate-type-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => setDraft((d) => ({ ...d, types: [...d.types, { code: "", label: "", price: 0 }] }))} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="rate-type-add">
                <Plus className="w-3.5 h-3.5" /> Add wash type
              </button>
              <label className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-600 inline-flex items-center gap-2">
                Currency
                <select className="ps-input w-24 font-mono h-8" value={draft.currency} onChange={(e) => setDraft((d) => ({ ...d, currency: e.target.value }))} data-testid="rate-card-currency">
                  <option>USD</option><option>EUR</option><option>GHS</option>
                </select>
              </label>
            </div>
            <div className="flex gap-2">
              <button className="ps-btn ps-btn-secondary" onClick={() => { setEditing(false); setDraft({ currency: card.currency, types: card.types.map((t) => ({ ...t })) }); }}>Cancel</button>
              <button className="ps-btn ps-btn-primary" onClick={save} disabled={saving} data-testid="save-wash-rate-card">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save rate card</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer-specific overrides */}
      <div className="mt-5 pt-4 border-t border-blue-200" data-testid="wash-overrides-section">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-900">Customer-specific overrides</div>
          {canEdit && (
            <button
              className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1"
              onClick={() => setOvEditor({ customer_code: "", currency: card.currency, rates: card.types.map((t) => ({ wash_category: t.code, unit_price: t.price })) })}
              data-testid="add-wash-override"
            >
              <Plus className="w-3.5 h-3.5" /> Add override
            </button>
          )}
        </div>
        {overrides.length === 0 ? (
          <div className="text-xs text-gray-500 italic">None — every customer bills at the depot rate card above.</div>
        ) : (
          <table className="w-full text-sm bg-white border border-gray-200">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.14em] text-gray-600 border-b border-gray-200">
                <th className="text-left py-1.5 px-2 w-40">Customer</th>
                <th className="text-left py-1.5 px-2">Rates</th>
                <th className="w-28"></th>
              </tr>
            </thead>
            <tbody>
              {overrides.map((t) => (
                <tr key={t.customer_code} className="border-b border-gray-100 last:border-0" data-testid={`wash-override-${t.customer_code}`}>
                  <td className="py-1.5 px-2 font-mono font-bold">{t.customer_code}</td>
                  <td className="py-1.5 px-2 font-mono text-xs">
                    {(t.rates || []).map((r) => `${r.wash_category} ${t.currency || "USD"} ${fmt(r.unit_price)}`).join(" · ") || "—"}
                  </td>
                  <td className="py-1.5 px-2 text-right whitespace-nowrap">
                    {canEdit && (
                      <>
                        <button className="text-xs text-blue-700 hover:underline mr-3" onClick={() => setOvEditor({ ...t })} data-testid={`edit-wash-override-${t.customer_code}`}>Edit</button>
                        <button className="text-xs text-rose-600 hover:underline" onClick={() => deleteOverride(t.customer_code)} data-testid={`delete-wash-override-${t.customer_code}`}>Remove</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {ovEditor && (
        <WashOverrideEditor
          initial={ovEditor}
          typeCodes={card.types.map((t) => t.code)}
          onClose={() => setOvEditor(null)}
          onSave={saveOverride}
        />
      )}
    </div>
  );
}

function RateTile({ label, value, sub, color, testid }) {
  return (
    <div className="border border-gray-200 bg-white rounded-sm px-3 py-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-xl font-display font-extrabold tabular-nums mt-0.5" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function WashOverrideEditor({ initial, typeCodes, onClose, onSave }) {
  const isNew = !initial.customer_code;
  const [code, setCode] = useState(initial.customer_code || "");
  const [currency, setCurrency] = useState(initial.currency || "USD");
  const [rates, setRates] = useState(initial.rates?.length ? initial.rates.map((r) => ({ ...r })) : [{ wash_category: typeCodes[0] || "", unit_price: 0 }]);
  const updateRow = (i, k, v) => setRates(rates.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const submit = () => {
    const cc = code.toUpperCase().trim().replace(/\s+/g, "_");
    if (!cc) { toast.error("Customer code is required"); return; }
    const cleaned = rates
      .map((r) => ({ wash_category: (r.wash_category || "").toUpperCase().trim(), unit_price: Number(r.unit_price) || 0 }))
      .filter((r) => r.wash_category);
    if (!cleaned.length) { toast.error("Add at least one rate"); return; }
    onSave({ customer_code: cc, currency: currency.toUpperCase(), rates: cleaned });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="wash-override-editor">
      <div className="bg-white border border-gray-300 max-w-lg w-full shadow-2xl mt-16">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-bold">{isNew ? "New customer override" : `Override — ${initial.customer_code}`}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-gray-600 mb-1">Customer code</label>
              <input className="ps-input font-mono uppercase" value={code} disabled={!isNew}
                onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="STC" data-testid="override-customer-code" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-gray-600 mb-1">Currency</label>
              <select className="ps-input font-mono" value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="override-currency">
                <option>USD</option><option>EUR</option><option>GHS</option>
              </select>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-[0.14em] text-gray-600">
              <th className="text-left py-1">Wash type</th>
              <th className="text-right py-1">Unit price</th>
              <th className="py-1"></th>
            </tr></thead>
            <tbody>
              {rates.map((r, i) => (
                <tr key={i} className="border-t border-gray-200">
                  <td className="py-1">
                    <select className="ps-input font-mono" value={r.wash_category} onChange={(e) => updateRow(i, "wash_category", e.target.value)} data-testid={`override-cat-${i}`}>
                      <option value="">— pick —</option>
                      {typeCodes.map((c) => <option key={c} value={c}>{c}</option>)}
                      {r.wash_category && !typeCodes.includes(r.wash_category) && <option value={r.wash_category}>{r.wash_category}</option>}
                    </select>
                  </td>
                  <td className="py-1"><input type="number" step="0.01" className="ps-input text-right font-mono" value={r.unit_price} onChange={(e) => updateRow(i, "unit_price", e.target.value)} data-testid={`override-price-${i}`} /></td>
                  <td className="py-1 text-right"><button onClick={() => setRates(rates.filter((_, idx) => idx !== i))} className="text-rose-600 text-xs"><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={() => setRates([...rates, { wash_category: "", unit_price: 0 }])} className="text-xs text-blue-700 hover:underline" data-testid="override-add-row">+ add rate</button>
        </div>
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="ps-btn ps-btn-secondary text-sm">Cancel</button>
          <button onClick={submit} className="ps-btn ps-btn-primary text-sm" data-testid="override-save"><Save className="w-4 h-4" /> Save override</button>
        </div>
      </div>
    </div>
  );
}
